// Slice 2B1 · Approved Trade Pricing section for Pricing Settings.
// Renders the tenant's approved-reusable PricingRules grouped by trade
// so the contractor can see and edit their saved installed_selling rates
// without touching Advanced Pricing Defaults, Starter Templates, global
// company markup, or supplier preferences. Editing preserves the same
// PricingRule id (no duplicates).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Save, Pencil, X, ExternalLink } from "lucide-react";

const INSTALLED_UNITS = ["sqft", "square", "lf", "item"];

const ApprovedTradePricingSection = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/onboarding/trade-setup/approved-rules");
      setData(r.data);
    } catch (e) {
      setData({ trades: [], total_rules: 0, error: true });
    }
  };
  useEffect(() => { refresh(); }, []);

  const startEdit = (rule) => {
    setEditingId(rule.pricing_rule_id);
    setEditDraft({
      selling_price_per_unit: rule.selling_price_per_unit,
      selling_price_unit: rule.selling_price_unit,
      name: rule.name || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };
  const saveEdit = async (rule) => {
    setSaving(true);
    try {
      const payload = {
        selling_price_per_unit: parseFloat(editDraft.selling_price_per_unit),
        selling_price_unit: editDraft.selling_price_unit,
      };
      const trimmedName = (editDraft.name || "").trim();
      if (trimmedName && trimmedName !== rule.name) payload.name = trimmedName;
      const r = await api.patch(
        `/onboarding/trade-setup/pricing-rule/${rule.pricing_rule_id}`,
        payload,
      );
      // The API returns the updated rule with the SAME id — belt/suspenders.
      if (r.data.pricing_rule_id !== rule.pricing_rule_id) {
        throw new Error("id_changed");
      }
      toast.success(t("pricing_extra.approved_rule_updated"));
      cancelEdit();
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || t("pricing_extra.save_failed");
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <section className="jc-card p-6" data-testid="approved-trade-pricing-loading">
        <div className="overline mb-2">{t("pricing_extra.approved_overline")}</div>
        <div className="text-sm text-[var(--jc-text-soft)]">{t("pricing_extra.loading")}</div>
      </section>
    );
  }

  const trades = data.trades || [];
  const empty = !trades.length;

  return (
    <section className="jc-card p-6"
             data-testid="approved-trade-pricing-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">{t("pricing_extra.approved_overline")}</div>
          <h3 className="font-display text-lg mt-1">{t("pricing_extra.approved_heading")}</h3>
          <p className="text-sm text-[var(--jc-text-soft)] mt-1 max-w-2xl leading-relaxed">
            {t("pricing_extra.approved_body")}
          </p>
        </div>
        <Link
          to="/app/setup?step=trade_setup"
          data-testid="approved-trade-pricing-open-trade-setup"
          className="shrink-0 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
        >
          {t("pricing_extra.open_trade_setup")} <ExternalLink size={12} />
        </Link>
      </div>

      {empty && (
        <div className="mt-4 rounded border border-[var(--jc-border)] bg-[var(--jc-bg)] p-4 text-sm"
             data-testid="approved-trade-pricing-empty">
          <div className="font-semibold text-[var(--jc-text)]">
            {t("pricing_extra.approved_empty_title")}
          </div>
          <div className="text-[var(--jc-text-soft)] mt-1">
            {t("pricing_extra.approved_empty_body_prefix")} <Link to="/app/setup?step=trade_setup" className="underline">{t("pricing_extra.approved_empty_body_link")}</Link> {t("pricing_extra.approved_empty_body_suffix")}
          </div>
        </div>
      )}

      {trades.map((tg) => (
        <div key={tg.trade}
             className="mt-4"
             data-testid={`approved-trade-pricing-trade-${tg.trade}`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--jc-text-soft)]">
            {tg.trade}
          </div>
          <div className="mt-2 space-y-2">
            {tg.rules.map((rule) => {
              const isEditing = editingId === rule.pricing_rule_id;
              return (
                <div key={rule.pricing_rule_id}
                     data-testid={`approved-trade-pricing-row-${rule.pricing_rule_id}`}
                     data-rule-id={rule.pricing_rule_id}
                     className="rounded border border-[var(--jc-border-strong)] bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold"
                           data-testid={`approved-trade-pricing-system-${rule.pricing_rule_id}`}>
                        {rule.system_type}
                      </div>
                      <div className="text-[11px] text-[var(--jc-text-soft)] mt-0.5">
                        {rule.name && rule.name !== rule.system_type ? rule.name : ""}
                      </div>
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => startEdit(rule)}
                        data-testid={`approved-trade-pricing-edit-${rule.pricing_rule_id}`}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                      >
                        <Pencil size={12} /> {t("pricing_extra.edit")}
                      </button>
                    )}
                  </div>

                  <dl className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <dt className="text-[var(--jc-text-soft)]">{t("pricing_extra.row_pricing")}</dt>
                      <dd className="font-semibold"
                          data-testid={`approved-trade-pricing-type-${rule.pricing_rule_id}`}>
                        {t("pricing_extra.row_installed")}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[var(--jc-text-soft)]">{t("pricing_extra.row_rate")}</dt>
                      <dd className="font-mono-jc font-semibold"
                          data-testid={`approved-trade-pricing-rate-${rule.pricing_rule_id}`}>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editDraft.selling_price_per_unit ?? ""}
                            onChange={(e) => setEditDraft((d) => ({ ...d, selling_price_per_unit: e.target.value }))}
                            data-testid={`approved-trade-pricing-rate-input-${rule.pricing_rule_id}`}
                            className="w-24 px-2 py-1 border border-[var(--jc-border-strong)] rounded"
                          />
                        ) : (
                          <>${Number(rule.selling_price_per_unit).toFixed(2)}</>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[var(--jc-text-soft)]">{t("pricing_extra.row_unit")}</dt>
                      <dd className="font-semibold"
                          data-testid={`approved-trade-pricing-unit-${rule.pricing_rule_id}`}>
                        {isEditing ? (
                          <select
                            value={editDraft.selling_price_unit}
                            onChange={(e) => setEditDraft((d) => ({ ...d, selling_price_unit: e.target.value }))}
                            data-testid={`approved-trade-pricing-unit-input-${rule.pricing_rule_id}`}
                            className="px-2 py-1 border border-[var(--jc-border-strong)] rounded"
                          >
                            {INSTALLED_UNITS.map((u) => (<option key={u} value={u}>{u}</option>))}
                          </select>
                        ) : (rule.selling_price_unit)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[var(--jc-text-soft)]">{t("pricing_extra.row_status")}</dt>
                      <dd className="font-semibold"
                          data-testid={`approved-trade-pricing-status-${rule.pricing_rule_id}`}>
                        {rule.active_for_estimates ? t("pricing_extra.active") : t("pricing_extra.inactive")} · {rule.estimate_picker_visibility === "reusable" ? t("pricing_extra.reusable") : t("pricing_extra.one_off")}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-3 rounded bg-emerald-50 border border-emerald-200 p-2 text-[11px] text-emerald-900"
                       data-testid={`approved-trade-pricing-copy-${rule.pricing_rule_id}`}>
                    <div className="font-semibold">
                      {t("pricing_extra.row_installed_headline")}
                    </div>
                    <div className="mt-0.5">
                      {t("pricing_extra.row_installed_body")}
                    </div>
                    <div className="mt-0.5 text-emerald-800">
                      {t("pricing_extra.row_installed_note")}
                    </div>
                  </div>

                  {isEditing && (
                    <div className="mt-3 flex items-center gap-2"
                         data-testid={`approved-trade-pricing-edit-actions-${rule.pricing_rule_id}`}>
                      <button
                        onClick={() => saveEdit(rule)}
                        disabled={saving}
                        data-testid={`approved-trade-pricing-save-${rule.pricing_rule_id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded jc-btn-primary disabled:opacity-60"
                      >
                        <Save size={12} /> {saving ? t("pricing_extra.saving") : t("pricing_extra.save_changes")}
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={saving}
                        data-testid={`approved-trade-pricing-cancel-${rule.pricing_rule_id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[var(--jc-border-strong)]"
                      >
                        <X size={12} /> {t("pricing_extra.cancel")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
};

export default ApprovedTradePricingSection;
